import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { BodyCopy } from "../../../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { ErrorAlertIcon as ErrorIcon } from "../../../assets/icons";
import { AuraLayout } from "../../../components/AuraLayout";
import { FlowHeroMark } from "../../../components/FlowHeroMark";
import { APP_ROUTES } from "../../../constants";
import { authPagePath } from "../../../auth/resume";
import { useStore } from "../../../store/globalState";
import { useSessionStore } from "../../../store/useSessionStore";
import { usePartnerStore } from "../../../store/usePartnerStore";
import { useRouteWithToken } from "../../../hooks/useRouteWithToken";
import {
  claimBlockMessageKey,
  claimBlockTitleKey,
  isOriginatorClosedReason,
  type ClaimBlockReason,
} from "../../../utils/bindErrors";
import { claimBlockReasonFromConfig } from "../../../utils/claimGate";
import { getEvents, safeCapture } from "../../../analytics";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";

interface ClaimBlockedLocationState {
  reason?: ClaimBlockReason;
}

/**
 * Shown when this payout-claim intention cannot be taken. Copy is opaque;
 * `reason` is analytics-only. Late / other occupant: Go to app. Originator
 * closed (`cancelled` / `sender_blocked`): support.
 */
export const ClaimBlockedPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const state = (location.state ?? {}) as ClaimBlockedLocationState;

  const configReason = claimBlockReasonFromConfig(session.data?.config);
  const reason: ClaimBlockReason = state.reason ?? configReason;
  const originatorClosed = isOriginatorClosedReason(reason);

  const colorConfig = theme.errorView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, iconColors, backgroundIcon } =
    colorConfig;

  useEffect(() => {
    safeCapture(getEvents().CLAIM_BLOCKED_VIEW, { reason });
  }, [reason]);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />

        <section className="flow-hero">

          <FlowHeroMark backgroundIcon={backgroundIcon}>

            <ErrorIcon size={60} colors={iconColors} />

          </FlowHeroMark>
        <section className="landing-container">
          <PageTitle
            highlight={t(claimBlockTitleKey(reason))}
            highlightColor={title}
            secudnary=""
            secudnaryColor={title}
          />
          <BodyCopy textColor={bodyCopy}>
            {t(claimBlockMessageKey(reason))}
          </BodyCopy>
        </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => {
              if (originatorClosed) {
                const partner = session.data?.partner;
                if (partner) {
                  usePartnerStore.getState().setPartnerInfo({
                    name: partner.name,
                    sessionExpiredRedirectUrl: partner.sessionExpiredRedirectUrl,
                    supportEmail: partner.supportEmail,
                    supportWhatsappNumber: partner.supportWhatsappNumber,
                  });
                }
                redirectTo(APP_ROUTES.SUPPORT_PAGE);
                return;
              }
              safeCapture(getEvents().EXIT_CLICK, { screen: "claim_blocked" });
              redirectTo(authPagePath({ kind: "transactions" }));
            }}
          >
            {originatorClosed ? t("common.needHelp") : t("error.goToApp")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
